/**
 * SOLID Review
 * S — Good. One use case: the game screen mounted — the entry (identity, the stored game, then the intent) and, beside it,
 *     the rules the table is played by. Its flows are private; what they share with other use cases is a collaborator.
 * O — Good. A new way into the game is one step in the entry route; a new rule the Domain's.
 * L — Strong. Implements its use-case role exactly.
 * I — Strong. `execute`.
 * D — Strong. The session's deps, the tab's records, the seat restore and the entry route, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type { ICommandPlanEntry, ICommandMeAnswered, ICommandResumeValidated, IEntityEntryPlan } from '../../domain/ports/schemas/SessionMoves';
import type { ICommandDiceRankingLoaded } from '../../domain/ports/schemas/DiceRankingMoves';
import type { IPayloadServerRoomCode } from '../ports/contracts';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import { DONE } from '../shared/ClientSteps';
import type { IConfigDepSession } from './IConfigDepSession';
import type { StoredRecords } from './StoredRecords';
import type { SeatRestore } from './SeatRestore';
import type { SessionEntryRoute } from './SessionEntryRoute';

export interface IConfigDepEnterGame extends IConfigDepSession {
  readonly records: StoredRecords;
  readonly seats: SeatRestore;
  readonly route: SessionEntryRoute;
}

/** How identity settled: known (or about to be named), signed out, or `me` could not be asked. */
type TIdentified = 'known' | 'signedOut' | 'unreachable';

const NOTHING: ICommandNothing = {};

/**
 * ENTERING THE GAME (FE2's four hooks from AppRoot — identity bootstrap, session recovery, cold-login rejoin, smart-join
 * routing; a use case since frontend-application-oo P3). One run per mount of the game screen (a second is refused while
 * one is in flight): identity and the stored game side by side — as the hooks ran — then, unless a seat was taken, what the
 * session intends (`SessionEntryRoute`). That the stored game goes before a code, rather than racing it, is FE2's (deviation,
 * flagged in §28.199). Beside the whole entry, the server's dice ranking is read and kept (frontend-domain-oo P2).
 *
 * Every server read that fails reads as "nothing", exactly as the hooks' port bindings mapped them: a failed lookup is no
 * room, a failed restore join is a game that is over, a failed round read is a table between rounds.
 */
export class EnterGameUseCase implements IUseCase<ICommandNothing, TPortOutcome> {
  constructor(private readonly deps: IConfigDepEnterGame) {}

  async execute(ctx: IContextRequest, _command: ICommandNothing): Promise<TPortOutcome> {
    const [entered] = await Promise.all([this.enter(ctx), this.keepDiceRanking(ctx)]);
    return entered;
  }

  /**
   * The server's dice ranking, read beside every entry and kept in the session — whether or not the entry is refused, so a
   * tab already seated has it too. Infrastructure asks the server once a page and answers later reads from that answer; a
   * read that failed changes nothing, and the next entry asks again.
   */
  private async keepDiceRanking(ctx: IContextRequest): Promise<void> {
    const { decisions, step, rounds } = this.deps;
    const served = await rounds.diceRanking(ctx);
    if (!served.success) return;
    const loaded: ICommandDiceRankingLoaded = { ranking: served.value };
    await step.run(ctx, (input) => decisions.diceRankingLoaded(ctx, input, loaded));
  }

  /** One entry into the game screen. */
  private async enter(ctx: IContextRequest): Promise<TPortOutcome> {
    const { decisions, step, records, route } = this.deps;
    const entry: ICommandPlanEntry = { stored: await records.read(ctx) };
    const plan = await step.run(ctx, (input) => decisions.planEntry(ctx, input, entry));
    if (!plan.success) return plan;
    try {
      const [identified] = await Promise.all([this.identify(ctx, plan.value.identity), this.resumeStored(ctx, plan.value)]);
      if (identified === 'unreachable') {
        await step.run(ctx, (input) => decisions.meUnreachable(ctx, input, NOTHING));
      } else if (identified === 'known') {
        await route.proceed(ctx, false);
      }
      return DONE;
    } finally {
      await step.run(ctx, (input) => decisions.entryFinished(ctx, input, NOTHING));
    }
  }

  /**
   * Who the tab is: nothing to do for a known player or a visitor about to name themselves. A token asks `me`: no player
   * behind it signs the tab out (FE3: through the account's sign-out, which forgets the stored game too); a server that could
   * not be asked leaves the account alone and stops the entry — until FE3 it read as "no player" and signed a registered
   * player out (§28.199).
   */
  private async identify(ctx: IContextRequest, identity: IEntityEntryPlan['identity']): Promise<TIdentified> {
    if (identity !== 'me') return 'known';
    const { decisions, step, auth, account } = this.deps;
    const me = await auth.me(ctx);
    if (!me.success) return 'unreachable';
    const answered: ICommandMeAnswered = { me: me.value };
    const decided = await step.run(ctx, (input) => decisions.meAnswered(ctx, input, answered));
    if (!decided.success || !decided.value.signOut) return 'known';
    await account.execute(ctx, NOTHING);
    return 'signedOut';
  }

  /** The stored game: checked by its code when it is this player's, then re-joined. */
  private async resumeStored(ctx: IContextRequest, plan: IEntityEntryPlan): Promise<void> {
    const { record } = plan;
    if (plan.resume === 'none' || record === null) return;
    if (plan.resume === 'validate') {
      const { decisions, step, rooms } = this.deps;
      const code: IPayloadServerRoomCode = { roomCode: record.roomCode };
      const found = await rooms.getRoomByCode(ctx, code);
      const lookup: ICommandResumeValidated['lookup'] = found.success && found.value !== null ? { roomStatus: found.value.roomStatus } : null;
      const validated: ICommandResumeValidated = { record, lookup };
      const checked = await step.run(ctx, (input) => decisions.resumeValidated(ctx, input, validated));
      if (!checked.success || !checked.value.proceed) return;
    }
    await this.deps.seats.restore(ctx, record);
  }
}
